import { Fragment, useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { safeGetAccessToken } from "../utils/auth";
import { toast } from "react-toastify";
import { ArrowRight, AlertTriangle } from "lucide-react";
import { API_BASE, TOASTPROPS } from "../shared/Constants";
import { reportHandledError, reportApiError } from "../utils/reportError";
import { Skeleton } from "./ui/skeleton";
import { compareModels } from "../utils/modelOrder";

interface Model {
  input_price: number;
  cached_input_price?: number;
  output_price: number;
  offpeak_input_price: number;
  offpeak_output_price: number;
  batch_input_price: number;
  batch_output_price: number;
  flex_input_price: number;
  flex_output_price: number;
  deprecation_date?: string | null;
  replacement_model?: string | null;
  [key: string]: any;
}
interface ModelList { [modelName: string]: Model }
type ExtractionLevel = "medium" | "advanced";
interface ExtractionTierInfo { price: number; description: string }
type FileExtractionInfo = Partial<Record<ExtractionLevel, ExtractionTierInfo>>;

const parsePrice = (p?: number): string =>
  typeof p === "number" && Number.isFinite(p)
    ? (p * 1e6).toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 4 })
    : "—";

const fmtCost = (p?: number): string => {
  if (p === undefined || p === null) return "-";
  if (p <= 0) return "Gratuito";
  return `R$ ${p.toFixed(3).replace(".", ",")} / página`;
};

const TIER_GROUPS: { key: string; label: string; discount?: string; description: string; docs?: string; in: keyof Model; out: keyof Model }[] = [
  { key: "std", label: "Padrão", description: "Tarifa padrão para chamadas à API.", in: "input_price", out: "output_price" },
  { key: "off", label: "Noturno", description: "Tarifa reduzida automática entre 22h e 6h (BRT), sem mudança de código.", discount: "-30%", in: "offpeak_input_price", out: "offpeak_output_price" },
  { key: "batch", label: "Batch", description: "Processamento assíncrono em lote: envie um arquivo JSONL e receba os resultados.", docs: "https://docs.maritaca.ai/pt/batch-api", discount: "-50%", in: "batch_input_price", out: "batch_output_price" },
  { key: "flex", label: "Flex", description: "Chamadas síncronas com prioridade reduzida, para tarefas que toleram maior latência.", docs: "https://docs.maritaca.ai/pt/flex-tier", discount: "-50%", in: "flex_input_price", out: "flex_output_price" },
];

// Ferramentas integradas (Data Ocean, busca na web, execução de código): cobradas
// por uso, somadas ao custo de tokens. Disponíveis no Sabiá 4 Thinking.
const TOOL_PRICES: { name: string; unit: string; cost: string }[] = [
  { name: "Busca na web", unit: "por busca", cost: "R$ 0,0165" },
  { name: "Leitura de página", unit: "por página lida", cost: "R$ 0,066" },
  { name: "Data Ocean", unit: "por GB processado", cost: "R$ 0,10" },
  { name: "Execução de código", unit: "por minuto", cost: "R$ 0,016" },
];

export default function Pricing() {
  const [loading, setLoading] = useState<boolean>(true);
  const [models, setModels] = useState<ModelList>({});
  const [extractionInfo, setExtractionInfo] = useState<FileExtractionInfo>({});
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();

  useEffect(() => {
    const get = async () => {
      try {
        setLoading(true);
        const token = await safeGetAccessToken({ getAccessTokenSilently, loginWithRedirect });
        if (!token) return;
        const headers = { Authorization: `Auth ${token}` };
        const res = await fetch(`${API_BASE}/api/chat/models`, { method: "GET", headers });
        if (!res.ok) {
          reportApiError(res, { feature: "pricing.fetch", method: "GET" });
          throw new Error("Failed");
        }
        const data = await res.json();
        setModels(data["models"] || {});
        const ex = await fetch(`${API_BASE}/api/info/file_extraction`, { method: "GET", headers });
        if (ex.ok) setExtractionInfo((await ex.json())["file_extraction_price"] || {});
        else { reportApiError(ex, { feature: "pricing.fetch", method: "GET" }); }
      } catch (error) {
        reportHandledError(error, { category: "api", feature: "pricing.fetch" });
        toast.error("Erro ao recuperar preços.", TOASTPROPS);
      } finally {
        setLoading(false);
      }
    };
    get();
  }, [getAccessTokenSilently]);

  const visible = Object.keys(models).filter((n) => !n.toLowerCase().includes("competidor")).sort(compareModels);
  const levels: { key: ExtractionLevel; label: string }[] = [
    { key: "medium", label: "Texto e tabelas" },
    { key: "advanced", label: "Com descrição visual" },
  ];

  return (
    <div className="pricing-essential space-y-8">
      <section aria-labelledby="model-pricing-title">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-5">
          <div>
            <h2 id="model-pricing-title" className="text-lg font-semibold">Preços por modelo</h2>
            <p className="mt-1 text-sm text-muted-foreground">Valores em reais por 1 milhão de tokens.</p>
          </div>
        </div>

        <div className="overflow-x-auto rounded-lg border border-border" role="region" aria-label="Comparação de preços" tabIndex={0}>
        <table id="model-pricing-table" aria-label="Preços por modelo" className="pricing-model-table w-full min-w-[1000px] table-fixed text-[13px]">
          <colgroup><col className="w-[20%]" />{Array.from({length:8}).map((_,i)=><col key={i} className="w-[8.75%]" />)}<col className="w-[10%]" /></colgroup>
          <thead className="bg-muted/40 text-xs text-muted-foreground">
            <tr>
              <th rowSpan={2} scope="col" className="px-4 py-3 text-left font-medium">Modelo</th>
              {TIER_GROUPS.map(item=><th key={item.key} scope="colgroup" colSpan={2} className="border-l border-border px-2 py-3 text-center font-medium text-foreground">
                {item.label}{item.discount && <span className="ml-2 text-[10px] font-normal text-muted-foreground">{item.discount}</span>}
              </th>)}
              <th rowSpan={2} scope="col" className="border-l border-border px-3 py-3 text-right font-medium">Entrada<br />em cache</th>
            </tr>
            <tr>{TIER_GROUPS.map(item=><Fragment key={item.key}>
              <th scope="col" className="border-l border-border px-3 pb-3 text-right font-normal">Entrada</th>
              <th scope="col" className="px-3 pb-3 text-right font-normal">Saída</th>
            </Fragment>)}</tr>
          </thead>
          <tbody>
            {loading ? Array.from({ length: 6 }).map((_, i) => (
              <tr key={i} className="h-[72px] border-t border-border/60">
                <td className="px-4 py-4"><Skeleton className="h-5 w-3/4" /></td>
                {Array.from({length:9}).map((_,j)=><td key={j} className={`px-3 py-4 ${j%2===0 ? "border-l border-border/60" : ""}`}><Skeleton className="ml-auto h-5 w-14" /></td>)}
              </tr>
            )) : visible.map(name=>{
              const model=models[name];
              return <tr key={name} className="h-[72px] border-t border-border/60 hover:bg-muted/30">
                <th scope="row" className="px-4 py-4 text-left font-medium">
                  <span className="[overflow-wrap:anywhere]">{name}</span>
                  {model.deprecation_date && <span className="mt-1 flex items-center gap-1 text-xs font-normal text-muted-foreground" title={`Descontinuação: ${model.deprecation_date}${model.replacement_model ? " — usar "+model.replacement_model : ""}`}>
                    <AlertTriangle className="h-3 w-3 shrink-0" />Até {model.deprecation_date.slice(8,10)}/{model.deprecation_date.slice(5,7)}/{model.deprecation_date.slice(0,4)}
                  </span>}
                </th>
                {TIER_GROUPS.map(item=><Fragment key={item.key}>
                  <td className="whitespace-nowrap border-l border-border/60 px-3 py-4 text-right tabular-nums">{parsePrice(model[item.in])}</td>
                  <td className="whitespace-nowrap px-3 py-4 text-right tabular-nums">{parsePrice(model[item.out])}</td>
                </Fragment>)}
                <td className="whitespace-nowrap border-l border-border/60 px-3 py-4 text-right tabular-nums">{parsePrice(model.cached_input_price)}</td>
              </tr>;
            })}
            {!loading && visible.length===0 && <tr><td colSpan={10} className="px-4 py-6 text-muted-foreground">Preços indisponíveis no momento.</td></tr>}
          </tbody>
        </table>
        </div>
        <p className="mt-3 text-xs leading-relaxed text-muted-foreground">Entrada em cache: a mesma tarifa nas quatro modalidades, sem acumular os descontos. Em telas menores, deslize a tabela para comparar todas as colunas.</p>
        <div className="mt-6 grid gap-4 text-xs sm:grid-cols-2 xl:grid-cols-4">
          {TIER_GROUPS.map(item=><div key={item.key}>
            <h3 className="font-medium">{item.label}</h3>
            <p className="mt-1 leading-relaxed text-muted-foreground">{item.description}</p>
            {item.docs && <a href={item.docs} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1 hover:underline">Documentação <ArrowRight className="h-3 w-3" /></a>}
          </div>)}
        </div>
      </section>

      <div className="space-y-4">
        <section aria-labelledby="extraction-pricing-title" className="overflow-hidden rounded-lg border border-border">
          <div className="bg-muted/40 px-5 py-4">
            <div>
              <h2 id="extraction-pricing-title" className="text-base font-medium">Extração de arquivos</h2>
              <p className="mt-1 text-sm text-muted-foreground">Custo por página extraída com OCR (mesmo preço nos dois níveis).</p>
            </div>
          </div>
          <div className="border-t border-border px-5 py-5 sm:px-6">
            {levels.map((level) => {
              const info = extractionInfo[level.key];
              return (
                <div key={level.key} className="grid gap-2 border-t border-border/60 py-4 sm:grid-cols-[1fr_auto] sm:gap-x-8">
                  <h3 className="text-sm font-medium">{level.label} <code className="ml-2 text-xs font-normal text-muted-foreground">{level.key}</code></h3>
                  <p className="text-sm tabular-nums sm:text-right">{loading ? "Carregando…" : fmtCost(info?.price)}</p>
                  <p className="text-sm leading-relaxed text-muted-foreground sm:col-span-2">{loading ? "Carregando descrição…" : info?.description || "Informação indisponível"}</p>
                </div>
              );
            })}
            <a href="https://docs.maritaca.ai/pt/files" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm font-medium hover:underline">
              Documentação de arquivos <ArrowRight className="h-3.5 w-3.5" />
            </a>
          </div>
        </section>

        <section aria-labelledby="tools-pricing-title" className="overflow-hidden rounded-lg border border-border">
          <div className="bg-muted/40 px-5 py-4">
            <div>
              <h2 id="tools-pricing-title" className="text-base font-medium">Ferramentas integradas</h2>
              <p className="mt-1 text-sm text-muted-foreground">Cobrança por uso, adicional ao custo de tokens.</p>
            </div>
          </div>
          <div className="border-t border-border px-5 py-5 sm:px-6">
            <p className="mb-3 text-sm text-muted-foreground">Disponíveis no Sabiá 4 Thinking.</p>
            <dl className="mb-4">
              {TOOL_PRICES.map((tool) => (
                <div key={tool.name} className="flex items-center justify-between gap-4 border-t border-border/60 py-4 text-sm">
                  <dt className="font-medium">{tool.name}<span className="mt-1 block font-normal text-muted-foreground">{tool.unit}</span></dt>
                  <dd className="shrink-0 tabular-nums">{tool.cost}</dd>
                </div>
              ))}
            </dl>
            <a href="https://docs.maritaca.ai/pt/ferramentas" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm font-medium hover:underline">
              Documentação de ferramentas <ArrowRight className="h-3.5 w-3.5" />
            </a>
          </div>
        </section>
      </div>
    </div>
  );
}
